import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {HTMLDialogElement | null} */
  #inlinePanel = null;
  /** @type {HTMLElement | null} */
  #inlineCard = null;
  /** @type {{ node: HTMLElement, hidden: boolean, inert: boolean }[]} */
  #hiddenCardChildren = [];
  #cardMinHeight = '';
  #cardHadNoNavigation = false;
  #requestId = 0;
  #ownsModal = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#cartUpdateAbortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    document.addEventListener('mvurra:quickshop-open', this.#handleOtherQuickshop);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#closeInlinePanel(false);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    document.removeEventListener('mvurra:quickshop-open', this.#handleOtherQuickshop);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = (event) => {
    this.#cachedContent.clear();
    const panel = this.#inlinePanel;
    if (!panel || !(event.target instanceof Node) || !panel.contains(event.target)) return;
    event.promise?.then(({ detail }) => {
      if (!detail?.didError && this.#inlinePanel === panel) {
        this.#closeInlinePanel(panel.contains(document.activeElement));
      }
    }).catch(() => {});
  };

  #handleOtherQuickshop = (event) => {
    if (event.detail?.owner === this) return;
    this.#requestId += 1;
    this.#abortController?.abort();
    this.querySelector('.quick-add__button--choose')?.removeAttribute('aria-busy');
    this.#closeInlinePanel(false);
    if (this.#ownsModal) this.#closeQuickAddModal();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    document.dispatchEvent(new CustomEvent('mvurra:quickshop-open', { detail: { owner: this } }));
    const requestId = ++this.#requestId;
    const button = this.querySelector('.quick-add__button--choose');
    button?.setAttribute('aria-busy', 'true');

    try {
      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (requestId !== this.#requestId || !this.isConnected) return;

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        if (!isMobileBreakpoint() && this.closest('product-card')) {
          this.#openInlinePanel(freshContent);
          return;
        }
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
        this.#openQuickAddModal();
      } else if (currentUrl) {
        window.location.href = currentUrl;
      }
    } catch (error) {
      if (error?.name !== 'AbortError' && currentUrl) window.location.href = currentUrl;
    } finally {
      if (requestId === this.#requestId) button?.removeAttribute('aria-busy');
    }
  };

  /** Keep the native picker and form together without carrying the PDP layout into the card. */
  #openInlinePanel(productGrid) {
    const card = this.closest('product-card');
    const productForm = productGrid.querySelector('product-form-component');
    if (!(card instanceof HTMLElement) || !productForm) return;
    this.#closeInlinePanel(false);

    const panel = document.createElement('dialog');
    panel.className = 'mv-inline-quickshop';
    panel.setAttribute('aria-label', this.dataset.productTitle || '');
    panel.setAttribute('open', '');
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'mv-inline-quickshop__close';
    close.setAttribute('aria-label', this.dataset.closeLabel || '');
    close.textContent = '×';
    close.addEventListener('click', () => this.#closeInlinePanel());
    panel.append(close);

    const content = document.createElement('product-component');
    content.className = 'mv-inline-quickshop__content';
    content.setAttribute('view-event-payload', productGrid.getAttribute('view-event-payload') || '');
    const picker = productGrid.querySelector('variant-picker');
    if (picker) content.append(picker);
    productForm.querySelectorAll('.accelerated-checkout-block, shopify-accelerated-checkout, .shopify-payment-button, .product__pickup-availabilities, pickup-availability, .payment-terms').forEach((node) => node.remove());
    content.append(productForm);
    panel.append(content);

    this.#cardMinHeight = card.style.minHeight;
    this.#cardHadNoNavigation = card.hasAttribute('data-no-navigation');
    card.style.minHeight = `${Math.ceil(card.getBoundingClientRect().height)}px`;
    this.#hiddenCardChildren = Array.from(card.children).filter((node) => node instanceof HTMLElement).map((node) => ({ node, hidden: node.hidden, inert: node.inert }));
    this.#hiddenCardChildren.forEach(({ node }) => { node.hidden = true; node.inert = true; });
    card.setAttribute('data-no-navigation', '');
    card.setAttribute('data-mv-quickshop-open', '');
    this.#inlineCard = card;
    this.#inlinePanel = panel;
    card.prepend(panel);

    // The native form subscribes to its nearest dialog during connection. Let it receive
    // picker updates before stopping those updates from changing the hidden card swatches.
    panel.addEventListener(StandardEvents.productSelect, (event) => event.stopPropagation());
    panel.addEventListener('click', (event) => event.stopPropagation());
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.#closeInlinePanel();
    });
    panel.addEventListener('cancel', (event) => { event.preventDefault(); this.#closeInlinePanel(); });
    close.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (this.#inlinePanel !== panel || !panel.isConnected) return;

      const panelTop = panel.getBoundingClientRect().top;
      const pageWrapper = card.closest('.page-wrapper');
      const scrollTarget = pageWrapper && /auto|scroll/.test(getComputedStyle(pageWrapper).overflowY) ? pageWrapper : window;
      const viewport = scrollTarget === window ? { top: 0, bottom: window.innerHeight } : scrollTarget.getBoundingClientRect();
      const header = document.querySelector('header-component[sticky]');
      const visibleTop = Math.max(0, viewport.top, header?.getBoundingClientRect().bottom ?? 0) + 12;
      const visibleBottom = Math.min(window.innerHeight, viewport.bottom) - close.offsetHeight - 12;
      if (panelTop >= visibleTop && panelTop <= visibleBottom) return;

      scrollTarget.scrollBy({
        top: panelTop - visibleTop,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    });
  }

  #closeInlinePanel(restoreFocus = true) {
    const card = this.#inlineCard;
    if (!card) return;
    this.#inlinePanel?.remove();
    this.#hiddenCardChildren.forEach(({ node, hidden, inert }) => { node.hidden = hidden; node.inert = inert; });
    card.style.minHeight = this.#cardMinHeight;
    card.toggleAttribute('data-no-navigation', this.#cardHadNoNavigation);
    card.removeAttribute('data-mv-quickshop-open');
    this.#inlinePanel = null;
    this.#inlineCard = null;
    this.#hiddenCardChildren = [];
    if (restoreFocus && this.isConnected) this.querySelector('.quick-add__button--choose')?.focus({ preventScroll: true });
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#ownsModal = true;
    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => { this.#ownsModal = false; }, { once: true });

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    this.#syncHeaderState(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Mirrors the header states that product media and details styles depend on. Those rules reach page
   * content from `#header-group` with a sibling combinator, which can't reach this body-level dialog.
   * Scroll is locked while the dialog is open, so the header can't change state until it closes.
   *
   * @param {QuickAddDialog} dialogComponent
   */
  #syncHeaderState(dialogComponent) {
    dialogComponent.toggleAttribute(
      'header-transparent',
      document.querySelector('header-component[transparent]') !== null
    );
    dialogComponent.toggleAttribute(
      'header-sticky-active',
      document.querySelector("#header-group #header-component[data-sticky-state='active']") !== null
    );
  }

  #closeQuickAddModal = () => {
    this.#closeInlinePanel(false);
    this.#ownsModal = false;
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

